/**
 * Сессия покупателя.
 *
 * Профиль грузится один раз и обновляется по требованию. Истёкший токен клиент
 * снимает сам (Api следит за 401), а здесь мы только реагируем — иначе человек
 * видел бы пустые экраны вместо экрана входа.
 */
import type { Profile } from '@allswipes/client';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { api } from './api';

interface SessionState {
  profile: Profile | null;
  loading: boolean;
  signedIn: boolean;
  refresh: () => Promise<void>;
  signOut: () => void;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(Boolean(api.session));

  const refresh = useCallback(async () => {
    if (!api.session) {
      setProfile(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setProfile(await api.me());
    } catch {
      // Токен отозван или протух — Api его уже снял.
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    return api.onSessionChange((token) => {
      if (!token) setProfile(null);
      else void refresh();
    });
  }, [refresh]);

  const value = useMemo<SessionState>(
    () => ({
      profile,
      loading,
      signedIn: Boolean(api.session && profile),
      refresh,
      signOut: () => api.setToken(null),
    }),
    [profile, loading, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession вызван вне SessionProvider');
  return ctx;
}
